import { AuthProvider, useAuth } from "@auth/AuthContext"
import { GuestRoute } from "@auth/GuestRoute"
import { ProtectedRoute } from "@auth/ProtectedRoute"
import { AppShell } from "@components/AppShell"
import { LegacyGradesRedirect, LegacyReviewRedirect } from "@components/LegacyGradesRedirect"
import { ProfessorAnalytics } from "@components/ProfessorAnalytics"
import { RouteErrorBoundary } from "@components/RouteErrorBoundary"
import { PageLoader } from "@components/ui/PageLoader"
import { ToastProvider } from "@components/ui/Toaster"
import { DirectorOnly } from "@features/direction"
import { isDemoHomeAccount } from "@features/home-demo"
import { OnboardingGate } from "@features/onboarding"
import { lazyRoute } from "@lib/lazyRoute"
import { LoginPage } from "@pages/Login"
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { ReactQueryDevtools } from "@tanstack/react-query-devtools"
import { Suspense } from "react"
import { createBrowserRouter, Navigate, Route, RouterProvider, Routes } from "react-router-dom"

import { classroomPlanningRoutes } from "@/constants/classroomPlanning"
import { lessonPlanRoutes } from "@/constants/lessonPlans"
import { RealtimeProvider } from "@/realtime"

// Lazy-loaded feature routes. One chunk per feature keeps the initial
// bundle small and the code-split boundaries aligned with feature folders.
const HomePage = lazyRoute("HomePage", () =>
  import("@features/home").then((m) => ({ default: m.HomePage })),
)
const ClassroomsPage = lazyRoute("ClassroomsPage", () =>
  import("@features/classrooms").then((m) => ({ default: m.ClassroomsPage })),
)
// Import the page directly (not via the feature index) so it gets its own
// chunk — going through the index would pull the whole feature's surface into
// the roster's chunk. Per .claude/rules/frontend-performance.md §A-2.
const StudentsPage = lazyRoute("StudentsPage", () =>
  import("@features/students/pages/StudentsPage").then((m) => ({ default: m.StudentsPage })),
)
// PRD-40 — the person-level page. Its own chunk (recharts + the note
// timeline) so the roster doesn't pay for it.
const StudentProfilePage = lazyRoute("StudentProfilePage", () =>
  import("@features/student-profile").then((m) => ({ default: m.StudentProfilePage })),
)
const StudentDetailRedirect = lazyRoute("StudentDetailRedirect", () =>
  import("@features/student-profile").then((m) => ({ default: m.StudentDetailRedirect })),
)
const MaterialsPage = lazyRoute("MaterialsPage", () =>
  import("@features/materials").then((m) => ({ default: m.MaterialsPage })),
)
const ReferencesPage = lazyRoute("ReferencesPage", () =>
  import("@features/references").then((m) => ({ default: m.ReferencesPage })),
)
const ClassSyllabusPage = lazyRoute("ClassSyllabusPage", () =>
  import("@pages/ClassSyllabusPage").then((m) => ({ default: m.ClassSyllabusPage })),
)
const ClassNotesPage = lazyRoute("ClassNotesPage", () =>
  import("@features/class-notes").then((m) => ({ default: m.ClassNotesPage })),
)
const ClassNoteDetailPage = lazyRoute("ClassNoteDetailPage", () =>
  import("@features/class-notes").then((m) => ({ default: m.ClassNoteDetailPage })),
)
const LessonPlansPage = lazyRoute("LessonPlansPage", () =>
  import("@features/lesson-plans").then((m) => ({ default: m.LessonPlansPage })),
)
const LessonPlanDetailPage = lazyRoute("LessonPlanDetailPage", () =>
  import("@features/lesson-plans").then((m) => ({ default: m.LessonPlanDetailPage })),
)
const MaterialDetailPage = lazyRoute("MaterialDetailPage", () =>
  import("@features/materials/pages/MaterialDetailPage").then((m) => ({
    default: m.MaterialDetailPage,
  })),
)
const SettingsPage = lazyRoute("SettingsPage", () =>
  import("@features/settings").then((m) => ({ default: m.SettingsPage })),
)
// PRD-41 §FR-4 — fullscreen review queue for a material's pending grades.
// Its own chunk: the two-pane screen is only ever reached from a "Revisar
// entregas (N)" CTA, so the material page shouldn't pay for it.
const ReviewQueuePage = lazyRoute("ReviewQueuePage", () =>
  import("@features/grading-review").then((m) => ({ default: m.ReviewQueuePage })),
)
// PRD-26 — notifications history page. Lazy so the bell + dropdown
// stay cheap on every authenticated render; the full table chunk
// loads only when the professor opens /notificaciones.
const NotificationsPage = lazyRoute("NotificationsPage", () =>
  import("@features/notifications").then((m) => ({ default: m.NotificationsPage })),
)
// PRD-17 — Panel de Dirección. Lazy-loaded per frontend-performance §A-1
// so Recharts (already bundled by the student-detail chunk) doesn't grow
// the initial bundle for the regular professor flow.
const DirectionPage = lazyRoute("DirectionPage", () =>
  import("@features/direction").then((m) => ({ default: m.DirectionPage })),
)
// REPLACE LATER: demo-only fallback shown for `admin@platonica.io`
// instead of the real HomePage. Lazy so it never enters the bundle for
// regular professors. Delete when the real HomePage reaches parity.
const DemoHomePage = lazyRoute("DemoHomePage", () =>
  import("@features/home-demo").then((m) => ({ default: m.DemoHomePage })),
)
const OnboardingPage = lazyRoute("OnboardingPage", () =>
  import("@features/onboarding").then((m) => ({ default: m.OnboardingPage })),
)
// PRD-15 — public, unauthenticated survey page. Lives outside
// <ProtectedRoute> on purpose: students don't have Platonica accounts
// and the JWT must not gate their submission flow.
const SurveyPage = lazyRoute("SurveyPage", () =>
  import("@features/public-surveys").then((m) => ({ default: m.SurveyPage })),
)
// PRD-36 — scan-to-grade surface (replaced PRD-19's text extractor).
// Lazy so heic2any (~150KB) and the rest of the feature only land in
// the bundle when reached.
const ScanGradePage = lazyRoute("ScanGradePage", () =>
  import("@features/ocr").then((m) => ({ default: m.ScanGradePage })),
)
// PRD-24 / ADR-0026 — public student photo upload portal. Lives outside
// <ProtectedRoute> on purpose: students don't have Platonica accounts,
// and the page authenticates via the URL token (the only public-mutating
// surface besides webhooks). Lazy so the SPA bundle never ships to a
// student's phone unless they actually open the link.
const EntregarPage = lazyRoute("EntregarPage", () =>
  import("@features/portal").then((m) => ({ default: m.EntregarPage })),
)
// PRD-33 / ADR-0030 — public account-activation (set-password) page. Lives
// outside <ProtectedRoute> on purpose: a newly-provisioned professor has no
// session yet and authenticates via the URL token. Lazy so the chunk only
// loads when an invitee opens the link.
const ActivarPage = lazyRoute("ActivarPage", () =>
  import("@features/account-activation").then((m) => ({ default: m.ActivarPage })),
)
// PRD-86 follow-up — public password-reset (set-password) page at
// /restablecer/:token. Sibling of /activar/:token: same public endpoints,
// reset copy. Outside <ProtectedRoute>; lazy so the chunk loads on demand.
const RestablecerPage = lazyRoute("RestablecerPage", () =>
  import("@features/account-activation").then((m) => ({ default: m.RestablecerPage })),
)

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 60_000, retry: 1, refetchOnWindowFocus: false },
  },
})

// REPLACE LATER: route-level switch that lets the demo account see a
// hand-crafted "Platonica at peak performance" home for pitch
// presentations. Drop the switch — and have `/` always render
// `<HomePage>` — once the real dashboard reaches feature parity.
function HomeRouter() {
  const { professor } = useAuth()
  if (isDemoHomeAccount(professor?.email)) return <DemoHomePage />
  return <HomePage />
}

// Keep the existing route tree while enabling supported navigation blockers.
const router = createBrowserRouter([{ path: "*", element: <AppRoutes /> }])

function AppRoutes() {
  // Outside Suspense: the boundary handles lazy route failures after loading.
  return (
    <RouteErrorBoundary>
      <ProfessorAnalytics />
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route
            path="/login"
            element={
              <GuestRoute>
                <LoginPage />
              </GuestRoute>
            }
          />
          <Route path="/encuesta" element={<SurveyPage />} />
          {/* PRD-24 / ADR-0026 — student photo upload portal. Public,
            token-authenticated, NOT wrapped in <ProtectedRoute>. */}
          <Route path="/entregar/:token" element={<EntregarPage />} />
          {/* PRD-33 / ADR-0030 — account-activation set-password page.
            Public, token-authenticated, NOT wrapped in
            <ProtectedRoute>. */}
          <Route path="/activar/:token" element={<ActivarPage />} />
          {/* PRD-86 follow-up — password-reset set-password page.
            Public, token-authenticated, NOT wrapped in
            <ProtectedRoute>. Sibling of /activar/:token. */}
          <Route path="/restablecer/:token" element={<RestablecerPage />} />
          <Route
            path="/bienvenida"
            element={
              <ProtectedRoute>
                <OnboardingPage />
              </ProtectedRoute>
            }
          />
          <Route
            element={
              <ProtectedRoute>
                <OnboardingGate>
                  <AppShell />
                </OnboardingGate>
              </ProtectedRoute>
            }
          >
            <Route path="/" element={<HomeRouter />} />
            <Route path="/salones" element={<ClassroomsPage />} />
            <Route path="/salones/:classroomId/alumnos" element={<StudentsPage />} />
            <Route path={classroomPlanningRoutes.syllabusPattern} element={<ClassSyllabusPage />} />
            <Route path="/salones/:classroomId/notas" element={<ClassNotesPage />} />
            <Route path={lessonPlanRoutes.workspacePattern} element={<LessonPlansPage />} />
            <Route path={lessonPlanRoutes.detailPattern} element={<LessonPlanDetailPage />} />
            <Route path="/salones/:classroomId/notas/:noteId" element={<ClassNoteDetailPage />} />
            {/* PRD-40 — the canonical per-student page. One person,
              every subject this professor teaches them. */}
            <Route path="/alumnos/:profileId" element={<StudentProfilePage />} />
            <Route
              path="/salones/:classroomId/alumnos/:studentId"
              element={<StudentDetailRedirect />}
            />
            <Route path="/materiales" element={<MaterialsPage />} />
            <Route path="/fuentes" element={<ReferencesPage />} />
            {/* PRD-64 — the sources page moved to the primary nav
              (2026-10-08); the first-week links keep working. */}
            <Route path="/materiales/fuentes" element={<Navigate to="/fuentes" replace />} />
            <Route path="/materiales/:materialId" element={<MaterialDetailPage />} />
            {/* PRD-41 §FR-4.1 — registered INSIDE the <AppShell>
              layout block on purpose: the shell mounts
              `useGradingCompletionInvalidator`, which is what keeps
              the queue fresh while the professor reviews. The page
              renders its own full-bleed two-pane container.
              `?upload=<id>` deep-links to one submission. */}
            <Route path="/materiales/:materialId/revision" element={<ReviewQueuePage />} />
            <Route path="/notificaciones" element={<NotificationsPage />} />
            {/* PRD-58 §FR-1.5 — back-compat redirects for the retired
              grades page (`/calificaciones`, its `/revisar/:id` queue
              and the older `/progreso` slug). Per
              .claude/rules/url-conventions.md §Migrations — keep the
              old paths as redirects for at least one release. */}
            <Route path="/calificaciones/revisar/:materialId" element={<LegacyReviewRedirect />} />
            <Route path="/calificaciones" element={<LegacyGradesRedirect />} />
            <Route path="/progreso" element={<LegacyGradesRedirect />} />
            <Route
              path="/direccion"
              element={
                <DirectorOnly>
                  <DirectionPage />
                </DirectorOnly>
              }
            />
            <Route path="/configuracion" element={<SettingsPage />} />
            <Route path="/herramientas/escanear" element={<ScanGradePage />} />
            {/* Back-compat redirect for the previous slug
              (`/herramientas/extraer-texto`). The page stopped being
              a text extractor in PRD-36; the slug followed. Per
              .claude/rules/url-conventions.md §Migrations — keep the
              old path as a redirect for at least one release. */}
            <Route
              path="/herramientas/extraer-texto"
              element={<Navigate to="/herramientas/escanear" replace />}
            />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </RouteErrorBoundary>
  )
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <AuthProvider>
          <RealtimeProvider>
            <RouterProvider router={router} />
          </RealtimeProvider>
        </AuthProvider>
      </ToastProvider>
      {/* bottom-left: the default sits exactly on the product's mobile FAB,
          so nobody on the team had seen the FAB in a phone-sized dev window. */}
      {import.meta.env.DEV && (
        <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-left" />
      )}
    </QueryClientProvider>
  )
}
